'use client';

import { useMemo } from 'react';
import { usePathname } from 'next/navigation';
import { useStore } from '@/store/store';
import { useGlobalContext } from '@/store/contexts/global-context';
import { getCookieValue } from '@/utils/cookies/client';
import { COOKIES } from '@/utils/cookies/constants';
import type { AmplitudeGlobalKeys } from '@/tracking/amplitude/amplitude-properties.types';
import { getPageNameFromPathname } from './page-name';

export type TrackingLoginStatus = AmplitudeGlobalKeys['Login Status'];

export type TrackingRouteContext = {
    pathname: string;
    pagePath: string;
    pageName: string;
    fullUrl?: string;
    hostname?: string;
    pageTitle?: string;
};

export type TrackingUserContext = {
    isLoggedIn: boolean;
    loginStatus: TrackingLoginStatus;
    jurisdiction?: string;
    nationalJurisdiction: AmplitudeGlobalKeys['National Jurisdiction'];
    accountId?: string;
    userIdCookie?: string;
};

export type TrackingPlatformContext = {
    isMobile: boolean;
    isAndroidWrapper: boolean;
    isIosWrapper: boolean;
    siteVersion: AmplitudeGlobalKeys['Site Version'];
    sitePlatform: AmplitudeGlobalKeys['Site Platform'];
    androidDistributionMethod: AmplitudeGlobalKeys['Android Distribution Method'];
};

export type TrackingUiContext = {
    isSearchOpen: boolean;
};

export type TrackingContext = {
    product: AmplitudeGlobalKeys['Product'];
    route: TrackingRouteContext;
    user: TrackingUserContext;
    platform: TrackingPlatformContext;
    ui: TrackingUiContext;
};

const getProduct = (): AmplitudeGlobalKeys['Product'] => {
    const product = process.env.PRODUCT;
    switch (product) {
        case 'casino':
            return 'casino_standalone_lobby';
        case 'poker':
            return 'poker_standalone_lobby';
        case 'xsell':
            return 'casino_xsell_lobby';
        default:
            return undefined;
    }
};

const getAndroidDistributionMethod = (): AmplitudeGlobalKeys['Android Distribution Method'] => {
    if (typeof navigator === 'undefined') {
        return '';
    }

    const ua = (navigator?.userAgent || '').toLowerCase();
    switch (true) {
        case ua.includes('selfdist'):
            return 'side-load';
        case ua.includes('playstore'):
            return 'play-store';
        default:
            return '';
    }
};

const getSiteVersion = ({
    isMobile,
    isWrapper
}: {
    isMobile: boolean;
    isWrapper: boolean;
}): AmplitudeGlobalKeys['Site Version'] => {
    switch (true) {
        case isMobile && !isWrapper:
            return 'mobile_web';
        case isMobile && isWrapper:
            return 'native';
        default:
            return 'desktop';
    }
};

const getSitePlatform = ({
    isMobile,
    isAndroidWrapper,
    isIosWrapper
}: {
    isMobile: boolean;
    isAndroidWrapper: boolean;
    isIosWrapper: boolean;
}): AmplitudeGlobalKeys['Site Platform'] => {
    const isWrapper = isAndroidWrapper || isIosWrapper;
    switch (true) {
        case isMobile && !isWrapper:
            return 'mobile_web';
        case isMobile && isWrapper:
            return isIosWrapper ? 'ios' : 'android';
        default:
            return 'desktop';
    }
};

export const useTrackingContext = (): TrackingContext => {
    const pathname = usePathname() || '/';
    const globals = useGlobalContext();
    const isSearchOpen = useStore((state) => state.search.isSearchOpen);
    const { accountID } = useStore((state) => state.userContext.accountSettings);

    return useMemo(() => {
        const loginStatus: TrackingLoginStatus = globals.isLoggedIn ? 'logged_in' : 'logged_out';
        const jurisdiction = globals.region ? String(globals.region).toLowerCase() : undefined;
        const nationalJurisdiction = (process.env.COUNTRY as AmplitudeGlobalKeys['National Jurisdiction']) || 'usa';
        const userIdCookie = getCookieValue(COOKIES.USER_ID) || undefined;

        const isAndroidWrapper = (globals.isXsellWrapper && globals.isAndroid) || globals.isAndroidWrapper;
        const isIosWrapper = (globals.isXsellWrapper && globals.isIos) || globals.isIosWrapper;

        const pageName = isSearchOpen ? 'search' : getPageNameFromPathname(pathname);
        const route: TrackingRouteContext = {
            pathname,
            pagePath: pathname,
            pageName,
            fullUrl: typeof window !== 'undefined' ? window.location.href : undefined,
            hostname: typeof window !== 'undefined' ? window.location.hostname : undefined,
            pageTitle: typeof window !== 'undefined' ? document.title : undefined
        };

        const platform: TrackingPlatformContext = {
            isMobile: globals.isMobile,
            isAndroidWrapper,
            isIosWrapper,
            siteVersion: getSiteVersion({
                isMobile: globals.isMobile,
                isWrapper: isAndroidWrapper || isIosWrapper
            }),
            sitePlatform: getSitePlatform({
                isMobile: globals.isMobile,
                isAndroidWrapper,
                isIosWrapper
            }),
            androidDistributionMethod: getAndroidDistributionMethod()
        };

        return {
            product: getProduct(),
            route,
            user: {
                isLoggedIn: globals.isLoggedIn,
                loginStatus,
                jurisdiction,
                nationalJurisdiction,
                accountId: accountID || undefined,
                userIdCookie
            },
            platform,
            ui: { isSearchOpen }
        };
    }, [
        pathname,
        isSearchOpen,
        globals.isLoggedIn,
        globals.region,
        globals.isMobile,
        globals.isXsellWrapper,
        globals.isAndroid,
        globals.isIos,
        globals.isAndroidWrapper,
        globals.isIosWrapper,
        accountID
    ]);
};
